import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { ZendeskArticle, ZendeskCategory } from 'interfaces/ZendeskArticle';

import { useContent } from 'hooks/queries/useContent';
import { useContentAccessError } from 'hooks/useContentAccessError';
import { NotFound } from 'components/app/NotFound';
import CenteredCircularProgress from 'components/page/CenteredCircularProgress';

import ArticleContainer from './ArticleContainer';
import CategoryContainer from './CategoryContainer';

// The API only ever resolves on the final segment of an address — everything before it
// is breadcrumb context, not part of the lookup.
const lastSegment = (splat?: string): string => {
  const segments = (splat ?? '').split('/').filter(Boolean);
  return segments[segments.length - 1] ?? '';
};

const ContentPage: FC = () => {
  const params = useParams();
  const content = lastSegment(params['*']);

  // getContent is fetched with handleErrorInPage, so the global interceptor never redirects
  // for it - ArticlePolicy::view deliberately tells 401 (sign in, you might get access) apart
  // from 403 (signed in, but this account has no My Tover access), and useContentAccessError
  // is what keeps that distinction from getting lost in the generic NotFound below.
  const { data, isLoading, error } = useContent(content);
  const accessError = useContentAccessError(error);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  if (accessError) {
    return accessError;
  }

  if (error || !data) {
    return <NotFound />;
  }

  return data.type === 'article' ? (
    <ArticleContainer article={data.data as ZendeskArticle} />
  ) : (
    <CategoryContainer category={data.data as ZendeskCategory} />
  );
};

export default ContentPage;
